import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import type { ChangedEntries } from './core/Core';
import type { Vessel } from './core/Vessel';
import type { PortCallEntry } from '@seaber/common/core/entries';
import type { PortCallEvent, TimeData } from './core/TimeData';
import {
  PortCallEventType,
  isPortCallArrivalEventTimeData,
  isPortCallBerthingEventTimeData,
  isPortCallDepartureEventTimeData,
} from './core/TimeData';
import type {
  PortCallUpdatesQuery,
  PortCallUpdatesQueryVariables,
  ScenarioTimeline_PortCallFragment,
  UpdatePortCallExternalTimestampsMutation,
  UpdatePortCallExternalTimestampsMutationVariables,
} from '../graphql';
import {
  PortcallExternalTimestampEvent,
  PortcallExternalTimestampType,
  PortCallUpdatesDocument,
  UpdatePortCallExternalTimestampsDocument,
} from '../graphql';
import { useApolloClient } from '@apollo/client';
import type { ClientId } from './core/ClientId';
import { parseClientId } from './core/ClientId';
import {
  assertDefined,
  exhaustiveCheck,
  get,
  isDefined,
  mapMap,
} from '../lib/util';
import { dateStringToNumOpt } from './core/util';
import { Badge, Box, Button, FormField, FormRow, Text } from './ui';
import { timeFieldFormat } from './ui/TimeField';
import type {
  PortCallUpdate,
  PortCallUpdateBase,
} from './PostCallUpdates.interface';
import {
  decodeSourcedFromIntoExternalSourceInfo,
  encodeExternalSourceIntoSourcedFrom,
} from './integrations/externalSourceEncodeDecode';
import { toast } from 'react-toastify';
import type { ModalProps } from './ui/Modal/Modal';
import Modal, { ModalBody, ModalFooter } from './ui/Modal/Modal';
import { FilterVessels, useVesselFilters } from './ScenarioComparison';
import { hasFeature, useCurrentUser } from '../state/zustand/';
import { mapGroupBy } from '@seaber/common/lib/collections';

export enum InternalPortCallTimeSource {
  Prospects = 'Prospects',
  Nor = 'NOR',
  Sof = 'SOF',
}

interface PortCallUpdateContextProps {
  availablePortCallUpdates: Map<ClientId, PortCallUpdate[]>;
  setAvailablePortCallUpdates: Dispatch<
    SetStateAction<Map<ClientId, PortCallUpdate[]>>
  >;
}

interface PortCallUpdateHandlerContextProps {
  applyPortCallUpdate: (update: PortCallUpdateBase) => void;
  clearPortCallUpdates: () => void;
  getPortCallUpdates: (
    portCallsById: Map<ClientId, PortCallEntry>,
  ) => Promise<string | undefined>;
  ignorePortCallUpdates: (portCallIds: ClientId[]) => void;
  applyAllPortCallUpdates: (opts: {
    onlyConfirmed: boolean;
    filterPortCall?: (portCallCID: ClientId) => boolean;
    filterUpdate?: (update: PortCallUpdateBase) => boolean;
  }) => void;
}

const externalTimestampTypeIntoEstimationType = {
  [PortcallExternalTimestampType.ESTIMATE]: 'estimate',
  [PortcallExternalTimestampType.REQUESTED]: 'requested',
  [PortcallExternalTimestampType.PLANNED]: 'planned',
  [PortcallExternalTimestampType.ACTUAL]: 'actual',
} as const;

const PortCallUpdateContext = React.createContext<
  PortCallUpdateContextProps | undefined
>(undefined);

const PortCallUpdateHandlerContext = React.createContext<
  PortCallUpdateHandlerContextProps | undefined
>(undefined);

export const PortCallUpdateProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const [availablePortCallUpdates, setAvailablePortCallUpdates] = useState<
    Map<ClientId, Array<PortCallUpdate>>
  >(new Map());

  const value = useMemo(
    () => ({
      availablePortCallUpdates,
      setAvailablePortCallUpdates,
    }),
    [availablePortCallUpdates, setAvailablePortCallUpdates],
  );

  return (
    <PortCallUpdateContext.Provider value={value}>
      {children}
    </PortCallUpdateContext.Provider>
  );
};

export const PortCallUpdateHandlerProvider = ({
  children,
  onApplyUpdate,
  onIgnoreUpdates,
}: {
  children: React.ReactNode;
  onApplyUpdate: (updates: PortCallUpdateBase[]) => void;
  onIgnoreUpdates: (portCallIds: ClientId[]) => void;
}): JSX.Element => {
  const apolloClient = useApolloClient();
  const isEtaApiAvailable = hasFeature('eta_api');
  const currentUser = useCurrentUser();
  const disablePortCallUpdates =
    !!currentUser?.organization?.configDisablePcUpdates;

  const portCallUpdateContext = useContext(PortCallUpdateContext);

  if (!portCallUpdateContext) {
    throw new Error(
      'PortCallUpdateHandlerProvider must be used inside PortCallUpdateProvider',
    );
  }

  const { availablePortCallUpdates, setAvailablePortCallUpdates } =
    portCallUpdateContext;

  const getPortCallUpdates = useCallback(
    async (
      portCallsById: Map<ClientId, PortCallEntry>,
    ): Promise<string | undefined> => {
      if (disablePortCallUpdates) {
        return;
      }
      const portCallClientIds = [...portCallsById.keys()];

      if (isEtaApiAvailable) {
        const now = Date.now();

        const recentPortCallIds = portCallClientIds.filter(
          (portCallClientId) => {
            const portCall = portCallsById.get(portCallClientId);
            if (!portCall) {
              return false;
            }

            // Check if the port call is recent. We consider a port call recent if
            // it has a planned departure within the last 14 days or if it has an
            // override that is within the last 14 days.
            const THRESHOLD_DAYS = 14;
            const THRESHOLD_MS = THRESHOLD_DAYS * 24 * 60 * 60 * 1000;

            const plannedDepartureTime = new Date(
              portCall.plannedDeparture,
            ).getTime();
            const timeSinceDeparture = now - plannedDepartureTime;

            const hasOverride = !!portCall.plannedDepartureOverride;
            const timeSinceOverrideDeparture =
              hasOverride && portCall.plannedDepartureOverride
                ? now - new Date(portCall.plannedDepartureOverride).getTime()
                : 0;

            if (
              timeSinceDeparture > THRESHOLD_MS ||
              (hasOverride && timeSinceOverrideDeparture > THRESHOLD_MS)
            ) {
              return false;
            }
            return true;
          },
        );

        // Skip the mutation entirely if there are no recent port calls
        if (recentPortCallIds.length > 0) {
          const updatePortCallTimestampsResult = await apolloClient
            .mutate<
              UpdatePortCallExternalTimestampsMutation,
              UpdatePortCallExternalTimestampsMutationVariables
            >({
              mutation: UpdatePortCallExternalTimestampsDocument,
              variables: {
                query: recentPortCallIds.map((portCallClientId) => ({
                  portCallClientId,
                })),
              },
              fetchPolicy: 'no-cache',
            })
            .catch((e) => {
              return {
                errors: `${e.name} ${e.message}`,
              };
            });

          if (updatePortCallTimestampsResult.errors) {
            toast.warn(`Problem fetching external port call timestamps`);
          }
        } else {
          return 'No update-eligible port calls found';
        }
      }

      const { data, error } = await apolloClient.query<
        PortCallUpdatesQuery,
        PortCallUpdatesQueryVariables
      >({
        query: PortCallUpdatesDocument,
        variables: {
          portCallClientIds,
        },
        fetchPolicy: 'no-cache',
      });

      if (error) {
        toast.warn(`Problem fetching port call updates`);
        return;
      }

      const incomingUpdates: PortCallUpdate[] = [];

      data.allPortCalls?.nodes?.filter(isDefined).forEach((pc) => {
        const portCall = portCallsById.get(parseClientId(pc.clientId));

        assertDefined(portCall, 'port call to fetch updates for not found');

        const portCallIncomingUpdates: PortCallUpdateBase[] = [];

        const portNotice = pc.portNoticesByPortCallId.nodes[0] ?? null;
        const portNoticeUpdatedAt =
          dateStringToNumOpt(portNotice?.sysPeriod.start?.value) ?? 0;
        const sof = pc.statementOfFactsByPortCallId?.nodes[0] ?? null;
        const sofUpdatedAt =
          dateStringToNumOpt(sof?.sysPeriod.start?.value) ?? 0;

        if (sof) {
          const ata = dateStringToNumOpt(sof.arrivalTime);
          const atb = dateStringToNumOpt(sof.berthed);
          const atd = dateStringToNumOpt(sof.sailingTime);

          if (ata) {
            portCallIncomingUpdates.push({
              portCallId: portCall.clientId,
              type: PortCallEventType.Arrival,
              value: ata,
              source: InternalPortCallTimeSource.Sof,
              sourceType: 'actual',
              updatedAt: sofUpdatedAt,
              isConfirmed: true,
            });
          }

          if (atb) {
            portCallIncomingUpdates.push({
              portCallId: portCall.clientId,
              type: PortCallEventType.Berthing,
              value: atb,
              source: InternalPortCallTimeSource.Sof,
              sourceType: 'actual',
              updatedAt: sofUpdatedAt,
              isConfirmed: true,
            });
          }

          if (atd) {
            portCallIncomingUpdates.push({
              portCallId: portCall.clientId,
              type: PortCallEventType.Departure,
              value: atd,
              source: InternalPortCallTimeSource.Sof,
              sourceType: 'actual',
              updatedAt: sofUpdatedAt,
              isConfirmed: true,
            });
          }
        }

        if (portNotice) {
          const eta = dateStringToNumOpt(portNotice.eta);
          const etb = dateStringToNumOpt(portNotice.berthingProspects);
          const etd = dateStringToNumOpt(portNotice.etdBerth);
          const ata = dateStringToNumOpt(portNotice.ataPbp);
          const atb = dateStringToNumOpt(portNotice.ataBerth);

          if (eta) {
            portCallIncomingUpdates.push({
              portCallId: portCall.clientId,
              type: PortCallEventType.Arrival,
              value: eta,
              source: InternalPortCallTimeSource.Prospects,
              sourceType: 'estimate',
              updatedAt: portNoticeUpdatedAt,
              isConfirmed: null,
            });
          }

          if (etb) {
            portCallIncomingUpdates.push({
              portCallId: portCall.clientId,
              type: PortCallEventType.Berthing,
              value: etb,
              source: InternalPortCallTimeSource.Prospects,
              sourceType: 'estimate',
              updatedAt: portNoticeUpdatedAt,
              isConfirmed: null,
            });
          }

          if (etd) {
            portCallIncomingUpdates.push({
              portCallId: portCall.clientId,
              type: PortCallEventType.Departure,
              value: etd,
              source: InternalPortCallTimeSource.Prospects,
              sourceType: 'estimate',
              updatedAt: portNoticeUpdatedAt,
              isConfirmed: null,
            });
          }

          if (ata) {
            portCallIncomingUpdates.push({
              portCallId: portCall.clientId,
              type: PortCallEventType.Arrival,
              value: ata,
              source: InternalPortCallTimeSource.Nor,
              sourceType: 'actual',
              updatedAt: portNoticeUpdatedAt,
              isConfirmed: null,
            });
          }

          if (atb) {
            portCallIncomingUpdates.push({
              portCallId: portCall.clientId,
              type: PortCallEventType.Berthing,
              value: atb,
              source: InternalPortCallTimeSource.Nor,
              sourceType: 'actual',
              updatedAt: portNoticeUpdatedAt,
              isConfirmed: null,
            });
          }
        }

        for (const externalUpdate of pc.portCallExternalTimestampsByPortCallId
          ?.nodes ?? []) {
          if (!externalUpdate) {
            continue;
          }

          portCallIncomingUpdates.push({
            portCallId: portCall.clientId,
            type: {
              [PortcallExternalTimestampEvent.ARRIVE]:
                PortCallEventType.Arrival,
              [PortcallExternalTimestampEvent.BERTH]:
                PortCallEventType.Berthing,
              [PortcallExternalTimestampEvent.DEPART]:
                PortCallEventType.Departure,
            }[externalUpdate.eventType],
            value: new Date(externalUpdate.value).getTime(),
            source: encodeExternalSourceIntoSourcedFrom(externalUpdate),
            sourceType:
              externalTimestampTypeIntoEstimationType[
                externalUpdate.timestampType
              ],
            updatedAt: new Date(externalUpdate.updatedAt).getTime(),
            isConfirmed: externalUpdate.isConfirmed,
          });
        }

        for (const update of portCallIncomingUpdates) {
          const latestConfirmedValueOfTheSameType = portCallIncomingUpdates
            .filter(
              (candidate) =>
                candidate.type === update.type && candidate.isConfirmed,
            )
            .reduce<PortCallUpdateBase | null>((acc, candidate) => {
              if (!acc) {
                return candidate;
              }
              if (acc.updatedAt < candidate.updatedAt) {
                return candidate;
              } else {
                return acc;
              }
            }, null);
          if (
            !update.isConfirmed &&
            latestConfirmedValueOfTheSameType &&
            latestConfirmedValueOfTheSameType.updatedAt > update.updatedAt
          ) {
            // Nothing to do here for you small update,
            // there is already a confirmed update of the same type with more powerfull updatedAt
            continue;
          }

          const currentSourcedAt =
            portCall[portCallEventTypeToEntryKeys[update.type].sourcedAtKey];
          const currentSourceName =
            portCall[portCallEventTypeToEntryKeys[update.type].sourcedFromKey];
          const currentOverride =
            portCall[portCallEventTypeToEntryKeys[update.type].overrideKey];
          const currentValue =
            portCall[portCallEventTypeToEntryKeys[update.type].valueKey];
          const value = currentOverride || currentValue;

          const isSourceSameAsCurrent = update.source === currentSourceName;
          const isValueSameAsCurrent = update.value === value;
          const valueDelta = update.value - value;
          const absDelta = Math.abs(valueDelta);
          const isNew =
            currentSourcedAt === null || currentSourcedAt < update.updatedAt;
          incomingUpdates.push({
            ...update,
            isNew,
            absDelta,
            valueDelta,
            isValueSameAsCurrent,
            isSourceSameAsCurrent,
          });
        }
      });

      // This is a small trick to make it possible to apply the same value for arrival/berth/departure
      // We add few milliseconds to berthing and departure so the system can follow the order of events
      // This addition is not visible by the user
      const incomingUpdatesShifted = incomingUpdates.map((incomingUpdate) => ({
        ...incomingUpdate,
        value:
          incomingUpdate.type === PortCallEventType.Arrival
            ? incomingUpdate.value
            : incomingUpdate.type === PortCallEventType.Berthing
              ? incomingUpdate.value + 10
              : incomingUpdate.type === PortCallEventType.Departure
                ? incomingUpdate.value + 20
                : exhaustiveCheck(incomingUpdate.type),
      }));

      setAvailablePortCallUpdates((prev) => {
        const newUpdates = mapGroupBy(
          incomingUpdatesShifted,
          (update) => update.portCallId,
        );
        const mergedUpdates = new Map(prev);
        [...newUpdates.entries()].forEach(([key, value]) =>
          mergedUpdates.set(key, value),
        );

        return mergedUpdates;
      });
    },
    [
      apolloClient,
      disablePortCallUpdates,
      isEtaApiAvailable,
      setAvailablePortCallUpdates,
    ],
  );

  const applyPortCallUpdate = useCallback(
    (update: PortCallUpdateBase) => {
      // Clear other updates of the same type
      setAvailablePortCallUpdates((prev) => {
        const prevUpdates = prev.get(update.portCallId);
        const newUpdates =
          prevUpdates?.filter(
            (prevUpdate) => prevUpdate.type !== update.type,
          ) ?? [];

        return new Map(prev).set(update.portCallId, newUpdates);
      });

      onApplyUpdate([update]);
    },
    [onApplyUpdate, setAvailablePortCallUpdates],
  );

  /** Ignore all updates related to given port calls */
  const ignorePortCallUpdates = useCallback(
    (portCallIds: ClientId[]) => {
      setAvailablePortCallUpdates((prev) => {
        portCallIds.forEach((portCallId) => prev.delete(portCallId));
        return new Map(prev);
      });

      onIgnoreUpdates([...portCallIds]);
    },
    [onIgnoreUpdates, setAvailablePortCallUpdates],
  );

  /** Ignore only selected individual updates */
  const ignoreUpdates = useCallback(
    (updatesToIgnore: PortCallUpdateBase[]) => {
      const affectedPortCalls: ClientId[] = [];

      setAvailablePortCallUpdates((prev) => {
        return new Map(
          mapMap(prev, (updates, portCallId) => {
            const remainingUpdates = updates.filter(
              (u) => !updatesToIgnore.includes(u),
            );
            if (updates.length !== remainingUpdates.length) {
              affectedPortCalls.push(portCallId);
            }
            return [portCallId, remainingUpdates];
          }),
        );
      });

      onIgnoreUpdates([...affectedPortCalls]);
    },
    [onIgnoreUpdates, setAvailablePortCallUpdates],
  );

  const applyAllPortCallUpdates = useCallback(
    ({
      onlyConfirmed,
      filterPortCall,
      filterUpdate,
    }: {
      onlyConfirmed: boolean;
      filterPortCall?: (portCallCID: ClientId) => boolean;
      filterUpdate?: (update: PortCallUpdateBase) => boolean;
    }) => {
      const updatesToApply: PortCallUpdateBase[] = [];
      const updatesToIgnore: PortCallUpdateBase[] = [];

      // Every port call
      for (const [portcallCID, portCallUpdates] of availablePortCallUpdates) {
        if (filterPortCall && !filterPortCall(portcallCID)) {
          continue;
        }

        const byType = mapGroupBy(
          portCallUpdates.filter(
            (u) => u.isNew && (!filterUpdate || filterUpdate(u)),
          ),
          (u) => u.type,
        );

        // One update of every type (arrival, berthing, departure)
        for (const [, updates] of byType) {
          let updateIndex: number;
          if (onlyConfirmed) {
            // Only allow a confirmed actual time
            updateIndex = updates.findIndex(
              (u) => u.sourceType === 'actual' && !!u.isConfirmed,
            );
          } else {
            // When unconfirmed times are OK, try to find any actual time
            updateIndex = updates.findIndex((u) => u.sourceType === 'actual');
            // Or fall back to any available time
            if (updateIndex === -1) {
              updateIndex = 0;
            }
          }
          const update = get(updates, updateIndex);
          if (update) {
            updatesToApply.push(update);
          }
          updatesToIgnore.push(...updates.filter((u) => u !== update));
        }
      }

      ignoreUpdates(updatesToIgnore);
      onApplyUpdate(updatesToApply);
    },
    [availablePortCallUpdates, ignoreUpdates, onApplyUpdate],
  );

  const clearPortCallUpdates = useCallback(
    () => setAvailablePortCallUpdates(new Map()),
    [setAvailablePortCallUpdates],
  );

  const value = useMemo(
    () => ({
      applyPortCallUpdate,
      clearPortCallUpdates,
      getPortCallUpdates,
      ignorePortCallUpdates,
      applyAllPortCallUpdates,
    }),
    [
      applyPortCallUpdate,
      clearPortCallUpdates,
      getPortCallUpdates,
      ignorePortCallUpdates,
      applyAllPortCallUpdates,
    ],
  );

  return (
    <PortCallUpdateHandlerContext.Provider value={value}>
      {children}
    </PortCallUpdateHandlerContext.Provider>
  );
};

export const timelinePortCallUpdateOpts = {
  onlyNewUpdates: true,
  noPastEstimates: true,
};

export const usePortCallUpdatesState = (
  portCallIds?: ClientId[],
  opts?: {
    onlyNewUpdates?: boolean;
    noPastEstimates?: boolean;
  },
): PortCallUpdateContextProps & {
  updates: PortCallUpdate[];
} => {
  const context = useContext(PortCallUpdateContext);

  if (!context) {
    throw new Error(
      'usePortCallUpdatesState must be used inside PortCallUpdateProvider',
    );
  }

  const updates = useMemo(() => {
    const now = new Date().valueOf();

    const updates = portCallIds
      ? (portCallIds
          .flatMap((id) => context.availablePortCallUpdates.get(id))
          .filter(isDefined) ?? [])
      : [...context.availablePortCallUpdates.values()].flatMap(
          (updates) => updates,
        );

    const filteredUpdates = opts
      ? updates.filter((update) => {
          if (opts?.onlyNewUpdates && !update.isNew) return false;

          if (
            opts?.noPastEstimates &&
            update.sourceType === 'estimate' &&
            update.value < now
          ) {
            return false;
          }

          return update.absDelta > 60_000;
        })
      : updates;

    return filteredUpdates;
  }, [context.availablePortCallUpdates, opts, portCallIds]);

  return {
    ...context,
    updates,
  };
};

export const usePortCallUpdates = (
  portCallIds?: ClientId[],
  opts?: {
    onlyNewUpdates?: boolean;
    noPastEstimates?: boolean;
  },
): PortCallUpdateContextProps &
  PortCallUpdateHandlerContextProps & {
    updates: PortCallUpdate[];
  } => {
  const portCallUpdatesState = usePortCallUpdatesState(portCallIds, opts);

  const handlerContext = useContext(PortCallUpdateHandlerContext);

  if (!handlerContext) {
    throw new Error(
      'usePortCallUpdates must be used inside PortCallUpdateHandlerProvider',
    );
  }

  return {
    ...portCallUpdatesState,
    ...handlerContext,
  };
};

export const portCallEventTypeToEntryKeys: Record<
  PortCallEventType,
  {
    valueKey: 'plannedArrival' | 'plannedBerthing' | 'plannedDeparture';
    overrideKey:
      | 'plannedArrivalOverride'
      | 'plannedBerthingOverride'
      | 'plannedDepartureOverride';
    sourcedFromKey:
      | 'plannedArrivalOverrideSourcedFrom'
      | 'plannedBerthingOverrideSourcedFrom'
      | 'plannedDepartureOverrideSourcedFrom';
    sourcedAtKey:
      | 'plannedArrivalOverrideSourcedAt'
      | 'plannedBerthingOverrideSourcedAt'
      | 'plannedDepartureOverrideSourcedAt';
  }
> = {
  Arrive: {
    valueKey: 'plannedArrival',
    overrideKey: 'plannedArrivalOverride',
    sourcedFromKey: 'plannedArrivalOverrideSourcedFrom',
    sourcedAtKey: 'plannedArrivalOverrideSourcedAt',
  },
  Berth: {
    valueKey: 'plannedBerthing',
    overrideKey: 'plannedBerthingOverride',
    sourcedFromKey: 'plannedBerthingOverrideSourcedFrom',
    sourcedAtKey: 'plannedBerthingOverrideSourcedAt',
  },
  Depart: {
    valueKey: 'plannedDeparture',
    overrideKey: 'plannedDepartureOverride',
    sourcedFromKey: 'plannedDepartureOverrideSourcedFrom',
    sourcedAtKey: 'plannedDepartureOverrideSourcedAt',
  },
};

export const addTimeSourcesToResult = (
  changeMaps: ChangedEntries,
  updates: PortCallUpdateBase[],
): ChangedEntries => {
  const { changedPortCalls: originalChangedPortCalls, ...restChanges } =
    changeMaps;
  const changedPortCalls = new Map(originalChangedPortCalls);

  for (const update of updates) {
    const { sourcedFromKey, sourcedAtKey } =
      portCallEventTypeToEntryKeys[update.type];
    const now = new Date().getTime();

    const change = changedPortCalls.get(update.portCallId);

    if (!change) {
      console.warn(
        `No portcall ${update.portCallId} on applying port call update time source. The port call might be archived.`,
      );
      continue;
    }

    const changeWithUpdatedSources: PortCallEntry = {
      ...change,
      [sourcedAtKey]: now,
      [sourcedFromKey]: update.source,
    };

    changedPortCalls.set(update.portCallId, changeWithUpdatedSources);
  }

  return {
    ...restChanges,
    changedPortCalls,
  };
};

export type PortCallTimeSourceType =
  | 'planned'
  | 'estimate'
  | 'actual'
  | 'requested';

export const getPortCallTimeSourceType = (
  portCall: PortCallEntry | ScenarioTimeline_PortCallFragment,
  eventType: PortCallEventType,
): PortCallTimeSourceType => {
  const { overrideKey, sourcedFromKey } =
    portCallEventTypeToEntryKeys[eventType];

  const sourcedFrom = portCall[sourcedFromKey];
  if (!portCall[overrideKey] || !sourcedFrom) return 'planned';

  switch (sourcedFrom) {
    case InternalPortCallTimeSource.Nor: {
      return 'estimate';
    }
    case InternalPortCallTimeSource.Prospects: {
      return 'estimate';
    }
    case InternalPortCallTimeSource.Sof: {
      return 'actual';
    }
    default: {
      const externalSourceInfo =
        decodeSourcedFromIntoExternalSourceInfo(sourcedFrom);
      if (externalSourceInfo) {
        const timestampType =
          externalTimestampTypeIntoEstimationType[
            externalSourceInfo.timestampType
          ];
        return timestampType;
      }

      console.error(
        'Unknown port call time source',
        portCall.clientId,
        sourcedFromKey,
        sourcedFrom,
      );

      return 'planned';
    }
  }
};

export const getPortCallSourceName = (
  sourcedFrom: string | null,
): string | null => {
  if (!sourcedFrom) {
    return null;
  }
  switch (sourcedFrom) {
    case InternalPortCallTimeSource.Nor: {
      return 'Notice of readiness';
    }
    case InternalPortCallTimeSource.Prospects: {
      return 'Prospects';
    }
    case InternalPortCallTimeSource.Sof: {
      return 'Statement of facts';
    }
    default: {
      const externalSourceInfo =
        decodeSourcedFromIntoExternalSourceInfo(sourcedFrom);
      if (externalSourceInfo) {
        return externalSourceInfo.name;
      }

      return null;
    }
  }
};

export const getPortCallSourceNameForPortCall = (
  portCall: PortCallEntry | ScenarioTimeline_PortCallFragment,
  eventType: PortCallEventType,
): string | null => {
  const { overrideKey, sourcedFromKey } =
    portCallEventTypeToEntryKeys[eventType];

  const sourcedFrom = portCall[sourcedFromKey];
  if (!portCall[overrideKey] || !sourcedFrom) return null;

  return getPortCallSourceName(sourcedFrom);
};

export const getRelatedEventForPortCallUpdate = (
  update: PortCallUpdateBase,
  eventsByPortCallId: Map<ClientId, TimeData[]>,
): TimeData<PortCallEvent> | null => {
  const events = eventsByPortCallId.get(update.portCallId) ?? [];
  const arrival = events.find(isPortCallArrivalEventTimeData);
  const berthing = events.find(isPortCallBerthingEventTimeData);
  const departure = events.find(isPortCallDepartureEventTimeData);

  return (
    (update.type === PortCallEventType.Arrival
      ? arrival
      : update.type === PortCallEventType.Berthing
        ? berthing
        : update.type === PortCallEventType.Departure
          ? departure
          : exhaustiveCheck(update.type, `Unexpected port call type`)) ?? null
  );
};

export const PortCallUpdateMenuItem = ({
  onClick,
  update,
  timezone,
  isInvalid,
}: {
  onClick: () => void;
  update: PortCallUpdate;
  timezone: string | undefined;
  isInvalid: boolean;
}): JSX.Element => {
  const isDisabled = isInvalid || update.isValueSameAsCurrent;

  return (
    <Box
      p={3}
      display="flex"
      flexDirection="row"
      alignItems="center"
      onClick={!isDisabled ? onClick : undefined}
      title={
        isInvalid
          ? 'Not possible to apply this timestamp'
          : update.isSourceSameAsCurrent
            ? 'Current source'
            : update.isConfirmed
              ? 'Value is confirmed'
              : update.isValueSameAsCurrent
                ? 'Value is the same'
                : undefined
      }
    >
      {update.isNew && !isDisabled ? (
        <Badge variant="status/unassigned" mr={2}>
          NEW
        </Badge>
      ) : null}
      {update.isConfirmed ? (
        <Badge variant="default" isRounded mr={2}>
          ✓
        </Badge>
      ) : null}
      {isInvalid && (
        <Box
          backgroundColor="red"
          width={5}
          height={5}
          borderRadius={5}
          mr={3}
        ></Box>
      )}
      <Text
        fontWeight={update.isSourceSameAsCurrent ? 'bold' : undefined}
        color={isDisabled ? 'neutral.gray_340' : undefined}
      >
        {update.type} ({getPortCallSourceName(update.source)}{' '}
        {update.sourceType}): {timeFieldFormat(update.value, timezone)}
      </Text>
    </Box>
  );
};

export interface AcceptAllUpdatesOptionsProps
  extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  vessels: Vessel[];
  onApply: (vessels: Vessel[]) => void;
}

export const AcceptAllUpdatesOptions = ({
  isOpen,
  onClose,
  onApply,
  vessels,
}: AcceptAllUpdatesOptionsProps): JSX.Element => {
  const useVesselFiltersResult = useVesselFilters(vessels);

  const { filteredVessels } = useVesselFiltersResult;

  return (
    <Modal width="40em" height="20em" isOpen={isOpen} onClose={onClose}>
      <ModalBody p={0} overflow="hidden" width="100%">
        <Box bg="neutral.white_100" p={4} width="100%">
          <FormRow>
            <FormField label="Vessels" width={160}>
              <FilterVessels
                vessels={vessels}
                useVesselFiltersResult={useVesselFiltersResult}
              />
            </FormField>
          </FormRow>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" mr="2" onPress={() => onClose()}>
          Cancel
        </Button>
        <Button variant="negative" onPress={() => onApply(filteredVessels)}>
          Apply
        </Button>
      </ModalFooter>
    </Modal>
  );
};
